import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtMoney } from './format';
import { c, chart } from '../../theme';

// ── Where the overtime sits ──────────────────────────────────────────────────
//
// The trend says whether overtime moved. This says where it sits. Same rows as
// the table below -- committed payroll flagged as overtime, summed on amount --
// cut by one dimension and expressed as a share.
//
// It was called "Premium mix", which was wrong twice over. The pack's premium
// mix panel decomposes straight-time, OT, double-time and fringe WITHIN each
// division: a pay-component breakdown this portal cannot build, because it
// receives no straight-time hours, holds no fringe rate, and never stores pay
// rate at all. Borrowing that name for a share-of-dollars chart invited a
// reader to believe the blocked tile had been delivered. The real one stays
// absent and named as missing in the calculation dictionary.
//
// It shows HR's labor categories, and is named for them. The table below opens
// on a different cut -- job family -- because pointing both at the same one made
// this a picture of the table rather than a second answer.
//
// The unmapped share is a segment like any other rather than a footnote. A
// composition chart that quietly sums to less than the total is the most
// convincing wrong answer on a page: it looks complete.

// theme.chart.coVisible -- four hues checked with the palette validator against
// this surface: lightness band, chroma floor, CVD separation (worst adjacent
// pair deutan dE 9.2) and normal-vision floor all pass. One warning stands, that
// the aqua sits at 2.74:1 against white, which the guidance discharges with
// visible labels or a table view; the legend below carries name, share and
// dollars, and the table under the panel is the exact-value view.
//
// Not the emphasis/recessive pair plus the sequential ramp it used before: those
// are a selection state and a magnitude ramp, and pressing them into service as
// categorical hues means two segments differing only by lightness.
const SEG_COLORS = chart.coVisible;
const CLASS_LABELS = { direct: 'Direct', indirect: 'Indirect' };

// `rows` lets the caller supply an aggregation it already holds -- the division
// split is computed from the cost-center totals the page has in hand, and
// re-deriving it here would create a second source that could disagree with the
// table for the same selection.
export default function CostShare({ scopeParams, filterQ = '', by = 'category', label = 'labor category', rows: providedRows }) {
  const qs = scopeParams && by ? `${scopeParams}&by=${by}${filterQ}` : null;

  const { data, isLoading: fetching } = useQuery({
    queryKey: ['ot-share', qs],
    queryFn:  () => api.get(`/overtime/by-role?${qs}`),
    enabled:  !!qs && !providedRows,
  });

  const isLoading = providedRows ? false : fetching;
  const rows = providedRows || [
    ...(data?.rows || []),
    ...(data?.unmapped ? [{ ...data.unmapped, key: 'Not in the mapping', isUnmapped: true }] : []),
  ];
  const total = rows.reduce((a, r) => a + Number(r.ot_amount), 0);

  const segments = rows.map((r, i) => ({
    key:    CLASS_LABELS[r.key] || r.key,
    amount: Number(r.ot_amount),
    pct:    total > 0 ? (Number(r.ot_amount) / total) * 100 : 0,
    color:  (r.isUnmapped || r.unassigned) ? c.warning : SEG_COLORS[i % SEG_COLORS.length],
  }));

  return (
    <>
      <div style={s.title}>Overtime cost by {label || by}</div>
      <div style={s.card}>
        {isLoading ? (
          <div style={s.empty}>Loading…</div>
        ) : !segments.length ? (
          <div style={s.empty}>No overtime in this selection.</div>
        ) : (
          <>
            <div style={s.bar}>
              {segments.map((seg) => (
                <div key={seg.key} title={`${seg.key} — ${fmtMoney(seg.amount)}`}
                     style={{ width: `${seg.pct}%`, background: seg.color }} />
              ))}
            </div>
            <div style={s.legend}>
              {segments.map((seg) => (
                <div key={seg.key} style={s.legendRow}>
                  <span style={{ ...s.swatch, background: seg.color }} />
                  <span style={s.legendKey}>{seg.key}</span>
                  <span style={s.legendPct}>{seg.pct.toFixed(1)}%</span>
                  <span style={s.legendAmt}>{fmtMoney(seg.amount)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

const s = {
  title:     { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
               color: c.textMuted, margin: '26px 0 10px' },
  card:      { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px',
               padding: '18px 16px', minHeight: '200px' },
  bar:       { display: 'flex', height: '22px', borderRadius: '5px', overflow: 'hidden',
               background: c.canvas },
  legend:    { marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '9px' },
  legendRow: { display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12px' },
  swatch:    { width: '10px', height: '10px', borderRadius: '2px', flex: 'none' },
  legendKey: { color: c.text, flex: 1, minWidth: 0, overflow: 'hidden',
               textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  legendPct: { color: c.textMuted, fontVariantNumeric: 'tabular-nums' },
  legendAmt: { color: c.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
               minWidth: '76px', textAlign: 'right' },
  empty:     { textAlign: 'center', padding: '28px', color: c.textFaint },
};
